import React from 'react';
import { GlassCard } from '../components/GlassCard';
import { Target, Shield, Network, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface WhyUsPageProps {
  onNavigate: (section: string) => void;
  isDark: boolean;
}
export function WhyUsPage({
  onNavigate,
  isDark
}: WhyUsPageProps) {
  return <div className={`page-transition pt-32 pb-16 px-6 lg:px-8 max-w-7xl mx-auto ${isDark ? 'text-white' : ''}`} data-id="element-364">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24" data-id="element-365">
        <div data-id="element-366">
          <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-367">
            Why Akios?
          </h1>
          <p className={`text-xl mb-8 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-368">
            The goal is simple: replace fragmented tools + spreadsheets with one
            clean system that gives owners complete visibility and control.
          </p>
          <Button size="lg" onClick={() => onNavigate('contact')} data-id="element-369">
            Schedule a Consultation
            <ArrowRight className="ml-2 w-4 h-4" data-id="element-370" />
          </Button>
        </div>
        <div className="relative" data-id="element-371">
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-500/20 to-purple-500/20 rounded-3xl blur-3xl -z-10" data-id="element-372" />
          <GlassCard className={`h-full flex items-center justify-center p-12 text-center ${isDark ? 'bg-gray-800/50 border-gray-700' : ''}`} data-id="element-373">
            <div data-id="element-374">
              <div className={`text-5xl font-bold mb-2 ${isDark ? 'text-brand-400' : 'text-brand-600'}`} data-id="element-375">
                99.99%
              </div>
              <div className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-376">
                System Uptime
              </div>

              <div className={`text-5xl font-bold mb-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-377">
                500+
              </div>
              <div className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-378">
                Enterprise Clients
              </div>

              <div className={`text-5xl font-bold mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-379">
                24/7
              </div>
              <div className={isDark ? 'text-gray-400' : 'text-gray-500'} data-id="element-380">
                Global Support
              </div>
            </div>
          </GlassCard>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-381">
        <GlassCard className={`${isDark ? 'bg-gradient-to-b from-gray-800 to-gray-900 border-gray-700' : 'bg-gradient-to-b from-white to-blue-50/30'}`} data-id="element-382">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'}`} data-id="element-383">
            <Target className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-700'}`} data-id="element-384" />
          </div>
          <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-385">
            Clarity
          </h3>
          <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-386">
            We believe in transparent systems. No black boxes. Our code is
            clean, documented, and built to be understood. We simplify
            complexity so you can focus on decision-making, not debugging.
          </p>
        </GlassCard>

        <GlassCard className={`${isDark ? 'bg-gradient-to-b from-gray-800 to-gray-900 border-gray-700' : 'bg-gradient-to-b from-white to-green-50/30'}`} data-id="element-387">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${isDark ? 'bg-green-900/50' : 'bg-green-100'}`} data-id="element-388">
            <Shield className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-700'}`} data-id="element-389" />
          </div>
          <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-390">
            Reliability
          </h3>
          <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-391">
            Downtime is not an option. We engineer for redundancy and
            resilience. Our systems are stress-tested to handle peak loads and
            unexpected failures without blinking.
          </p>
        </GlassCard>

        <GlassCard className={`${isDark ? 'bg-gradient-to-b from-gray-800 to-gray-900 border-gray-700' : 'bg-gradient-to-b from-white to-purple-50/30'}`} data-id="element-392">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${isDark ? 'bg-purple-900/50' : 'bg-purple-100'}`} data-id="element-393">
            <Network className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-700'}`} data-id="element-394" />
          </div>
          <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-395">
            Systems Thinking
          </h3>
          <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-396">
            We see the whole picture. Your ERP isn't just software; it's the
            nervous system of your company. We design holistic solutions that
            integrate seamlessly with your human workflows.
          </p>
        </GlassCard>
      </div>
    </div>;
}